import React, { useState, useEffect, useCallback } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { loadStripe } from "@stripe/stripe-js";
import {
  Elements,
  CardElement,
  useStripe,
  useElements,
} from "@stripe/react-stripe-js";
import { getCustomerToken } from "../utils/CustomerAuth";
import { CartServices } from "../services/CartServices";
import { CouponServices } from "../services/CouponServices";
import { OrderServices } from "../services/OrderServices";
import { DeliveryAddressServices } from "../services/deliveryAddressServices";
import OrderConfirm from "./OrderConfirm";
import CheckoutCSS from "../helper/CheckoutStyles";

let stripePromise = null;

const getStripePromise = () => {
  if (!stripePromise) {
    const key = process.env.REACT_APP_STRIPE_KEY;
    if (!key) {
      console.error("Stripe key is missing from .env");
      return null;
    }
    stripePromise = loadStripe(key);
  }
  return stripePromise;
};

const API_BASE = "https://api.ifjona.com";

const toNumber = (val) => {
  const n = Number(String(val ?? "").replace(/[^\d.]/g, ""));
  return Number.isFinite(n) ? n : 0;
};

const money = (n, currencySymbol = "$") =>
  `${currencySymbol}${Number(n || 0).toFixed(2)}`;

const getCurrencySymbol = (item) => {
  if (item?.resolved_price?.currency_symbol) return item.resolved_price.currency_symbol;
  if (item?.location?.currency?.symbol) return item.location.currency.symbol;
  if (item?.locationProductPrice?.location?.currency?.symbol) return item.locationProductPrice.location.currency.symbol;
  if (item?.productInfoLocationPrice?.location?.currency?.symbol) return item.productInfoLocationPrice.location.currency.symbol;
  return "$";
};

const toImageUrl = (value) => {
  if (!value) return null;
  const s = String(value).trim();
  if (s.startsWith("http://") || s.startsWith("https://")) return s;
  if (s.startsWith("/storage/")) return `${API_BASE}${s}`;
  if (s.startsWith("storage/")) return `${API_BASE}/${s}`;
  return `${API_BASE}/storage/${s.replace(/^\/+/, "").replace(/^storage\//, "")}`;
};

const resolveImage = (item) =>
  toImageUrl(
    (item?.product_info || item?.productInfo)?.image || item?.product?.image
  );

const resolveTitle = (item) => item?.product?.title || "Product";

const resolveUnitPrice = (item) => {
  if (item?.resolved_price?.unit_price) {
    return toNumber(item.resolved_price.unit_price);
  }

  const productInfo = item?.product_info || item?.productInfo;
  const product = item?.product;

  if (
    item?.locationProductPrice?.flash_sales_price != null &&
    item.locationProductPrice.flash_sales_price !== ""
  ) {
    return toNumber(item.locationProductPrice.flash_sales_price);
  }

  if (
    productInfo?.flash_sale_price != null &&
    productInfo.flash_sale_price !== ""
  ) {
    return toNumber(productInfo.flash_sale_price);
  }

  if (product?.flash_sale_price != null && product.flash_sale_price !== "") {
    return toNumber(product.flash_sale_price);
  }

  const override = productInfo?.current_price_override ?? null;
  if (override !== null && override !== "") {
    return toNumber(override);
  }

  if (item?.locationProductPrice?.base_current_price != null) {
    return toNumber(item.locationProductPrice.base_current_price);
  }

  return toNumber(product?.base_current_price ?? 0);
};
const resolveUnitTax = (item) => {
  return toNumber(
    item?.locationProductPrice?.tax ??
    item?.productInfoLocationPrice?.tax ??
    item?.resolved_price?.tax ??
    0
  );
};

const hasFlashSale = (item) => {
  if (item?.resolved_price?.is_flash_sale) return true;

  if (
    item?.locationProductPrice?.flash_sales_price != null &&
    item.locationProductPrice.flash_sales_price !== ""
  ) {
    return true;
  }

  const productInfo = item?.product_info || item?.productInfo;
  const product = item?.product;

  return (
    (productInfo?.flash_sale_price != null &&
      productInfo.flash_sale_price !== "") ||
    (product?.flash_sale_price != null && product.flash_sale_price !== "")
  );
};

const StripePaymentForm = ({ paymentPayload, onSuccess, onError }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [paying, setPaying] = useState(false);

  const handlePay = async () => {
      if (!stripe || !elements || !paymentPayload) return;

      setPaying(true);

      try {
        const { client_secret } = await OrderServices.createPaymentIntent(paymentPayload);

        const result = await stripe.confirmCardPayment(client_secret, {
          payment_method: {
            card: elements.getElement(CardElement),
          },
        });

        if (result.error) {
          onError(result.error.message);
        } else if (result.paymentIntent.status === "succeeded") {
          await onSuccess(result.paymentIntent);
        }
      } catch (err) {
        onError(err?.response?.data?.message || "Payment failed.");
      } finally {
        setPaying(false);
      }
  };

  return (
    <div>
      <div
        style={{
          border: "1px solid #e4e7ec",
          borderRadius: 8,
          padding: "14px 16px",
          background: "#fff",
          marginBottom: 16,
        }}
      >
        <CardElement
          options={{
            style: {
              base: {
                fontSize: "14px",
                color: "#1d2939",
                "::placeholder": { color: "#b0b8c8" },
              },
            },
          }}
        />
      </div>

      <button
        type="button"
        className={`btn btn-main py-14 w-100 rounded-8${paying ? " opacity-50" : ""}`}
        disabled={paying || !stripe}
        onClick={handlePay}
      >
        {paying ? "Processing Payment..." : "Pay Now"}
      </button>
    </div>
  );
};

const CSS = `
  .co-label     { font-size:11px; letter-spacing:.7px; text-transform:uppercase; color:#98a2b3; font-weight:800; }
  .co-hint      { background:#f9f9f9; border:1px dashed #ddd; border-radius:8px; padding:9px 14px; font-size:12px; color:#98a2b3; font-weight:600; text-align:center; margin:0 0 12px; }
  .co-toolbar   { background:#fafafa; border:1px solid #e8e8e8; border-radius:8px; padding:10px 16px; margin-bottom:20px; display:flex; align-items:center; justify-content:space-between; flex-wrap:wrap; gap:10px; }
  .co-toolbar-title { font-size:13px; font-weight:800; color:#344054; letter-spacing:.3px; }
  .co-input     { width:100%; border:1px solid #e4e7ec; border-radius:8px; padding:11px 14px; font-size:13.5px; color:#1d2939; outline:none; transition:border .15s; background:#fff; }
  .co-input:focus { border-color:#f27b21; box-shadow:0 0 0 3px rgba(242,123,33,.12); }
  .co-input::placeholder { color:#b0b8c8; }
  .co-input[readonly] { background:#f8fafc; }
  textarea.co-input { resize:vertical; }
  .co-item-row  { display:flex; align-items:center; justify-content:space-between; gap:12px; padding:14px 0; border-bottom:1px solid #f5f5f5; }
  .co-item-row:last-child { border-bottom:none; }
  .co-item-img  { width:60px; height:60px; object-fit:cover; border-radius:8px; border:1px solid #ececec; flex-shrink:0; }
  .co-item-title{ font-size:13px; font-weight:700; color:#1d2939; line-height:1.4; }
  .co-item-qty  { font-size:12px; color:#98a2b3; font-weight:600; margin-top:3px; }
  .co-item-price{ font-size:14px; font-weight:800; color:#1d2939; white-space:nowrap; flex-shrink:0; }
  .co-item-price.co-flash { color:#dc2626; }
  .co-pay-item  { border:1px solid #e8e8e8; border-radius:8px; margin-bottom:10px; overflow:hidden; }
  .co-pay-label { display:flex; align-items:center; gap:10px; padding:13px 16px; cursor:pointer; font-size:13.5px; font-weight:700; color:#344054; margin:0; }
  .co-pay-label input[type=radio] { accent-color:#f27b21; width:16px; height:16px; flex-shrink:0; }
  .co-pay-desc  { padding:12px 16px 14px; background:#fffdf8; border-top:1px solid #fcd9b8; font-size:12.5px; color:#667085; line-height:1.7; }
  .co-coupon-badge { display:inline-flex; align-items:center; gap:8px; background:#f0fdf4; border:1px solid #bbf7d0; border-radius:8px; padding:6px 12px; font-size:12px; font-weight:700; color:#166534; }
  .co-error { background:#fef2f2; border:1px solid #fecaca; border-radius:8px; padding:10px 14px; font-size:13px; color:#dc2626; margin-top:12px; }
  .co-flash-badge { display:inline-block; background:#fef2f2; color:#dc2626; font-size:9px; font-weight:700; padding:2px 6px; border-radius:10px; margin-left:6px; }
`;

const Checkout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const selectedIds = location.state?.selectedIds ?? [];

  const [loading, setLoading] = useState(true);
  const [orderItems, setOrderItems] = useState([]);
  const [selectedPayment, setSelectedPayment] = useState("stripe");
  const [placing, setPlacing] = useState(false);
  const [error, setError] = useState("");

  const [currencySymbol, setCurrencySymbol] = useState("$");

  const [couponInput, setCouponInput] = useState(location.state?.coupon ?? "");
  const [couponData, setCouponData] = useState(null);
  const [couponError, setCouponError] = useState("");
  const [couponLoading, setCouponLoading] = useState(false);

  const [orderSuccess, setOrderSuccess] = useState(null);
  const [placedOrderId, setPlacedOrderId] = useState(null);
  const [step, setStep] = useState("form");

  const [locations, setLocations] = useState([]);
  const [states, setStates] = useState([]);
  const [filteredStates, setFilteredStates] = useState([]);
  const [selectedLocationObj, setSelectedLocationObj] = useState(null);
  const [selectedStateObj, setSelectedStateObj] = useState(null);
  const [shippingCharge, setShippingCharge] = useState(0);
  const [locationLoading, setLocationLoading] = useState(false);

  const [pendingOrderPayload, setPendingOrderPayload] = useState(null);

  const [form, setForm] = useState({
    firstName: "",
    lastName: "",
    businessName: "",
    country: "",
    address: "",
    addressLine2: "",
    city: "",
    state: "",
    postCode: "",
    phone: "",
    email: "",
    notes: "",
  });

  useEffect(() => {
    if (!getCustomerToken()) {
      navigate("/login", { state: { from: "/checkout" } });
    }
  }, [navigate]);

  const loadOrderItems = useCallback(async () => {
    if (!selectedIds.length) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const allItems = await CartServices.getCart();
      const arr = Array.isArray(allItems) ? allItems : [];
      const filtered = arr.filter((item) => selectedIds.includes(String(item.id)));
      setOrderItems(filtered);

      if (filtered.length > 0) {
        setCurrencySymbol(getCurrencySymbol(filtered[0]));
      }
    } catch (err) {
      console.error(err);
      if ([401, 403].includes(err?.response?.status)) {
        navigate("/login", { state: { from: "/checkout" } });
      }
    } finally {
      setLoading(false);
    }
  }, [selectedIds, navigate]);

  useEffect(() => {
    loadOrderItems();
  }, [loadOrderItems]);

  useEffect(() => {
    const loadLocationsStatesAndSavedAddress = async () => {
      try {
        setLocationLoading(true);

        const [locationRes, stateRes, addressRes] = await Promise.allSettled([
          OrderServices.getLocations(),
          OrderServices.getStates(),
          DeliveryAddressServices.getAddress(),
        ]);

        const locationData =
          locationRes.status === "fulfilled" && Array.isArray(locationRes.value)
            ? locationRes.value
            : [];

        const stateData =
          stateRes.status === "fulfilled" && Array.isArray(stateRes.value)
            ? stateRes.value
            : [];

        const activeLocations = locationData.filter((loc) => {
          if (loc?.is_active === undefined || loc?.is_active === null) return true;
          return loc?.is_active === true || loc?.is_active === 1 || loc?.is_active === "1";
        });

        setLocations(activeLocations);
        setStates(stateData);

        const savedAddress =
          addressRes.status === "fulfilled" ? addressRes.value?.data : null;

        if (savedAddress) {
          setForm((prev) => ({
            ...prev,
            firstName: savedAddress.first_name || "",
            lastName: savedAddress.last_name || "",
            businessName: savedAddress.business_name || "",
            country: savedAddress.country_id ? String(savedAddress.country_id) : "",
            address: savedAddress.address || "",
            addressLine2: savedAddress.address_line2 || "",
            city: savedAddress.city || "",
            state: savedAddress.state_id ? String(savedAddress.state_id) : "",
            postCode: savedAddress.post_code || "",
            phone: savedAddress.phone || "",
            email: savedAddress.email || "",
          }));
        }
      } catch (err) {
        console.error("Failed to load locations/states/address", err);
      } finally {
        setLocationLoading(false);
      }
    };

    loadLocationsStatesAndSavedAddress();
  }, []);

  useEffect(() => {
    const selectedLocation = locations.find(
      (loc) => String(loc.id) === String(form.country)
    );

    setSelectedLocationObj(selectedLocation || null);

    const nextStates = states.filter(
      (st) => String(st.location_id) === String(form.country)
    );

    setFilteredStates(nextStates);
  }, [form.country, locations, states]);

  useEffect(() => {
    const selectedState = filteredStates.find(
      (st) => String(st.id) === String(form.state)
    );

    setSelectedStateObj(selectedState || null);
    setShippingCharge(Number(selectedState?.delivery_charge || 0));
  }, [form.state, filteredStates]);

  const subtotal = orderItems.reduce(
    (sum, item) => sum + resolveUnitPrice(item) * Number(item?.quantity ?? 1),
    0
  );

  const originalTax = orderItems.reduce(
    (sum, item) => sum + resolveUnitTax(item) * Number(item?.quantity ?? 1),
    0
  );

  const couponDiscountValue = Number(couponData?.discount || 0);
  const couponPercentageValue = Number(couponData?.percentage || 0);

  const rawDiscount =
    couponDiscountValue > 0
      ? couponDiscountValue
      : couponPercentageValue > 0
      ? subtotal * (couponPercentageValue / 100)
      : 0;

  // discount subtotal er beshi hole subtotal porjonto cap kore dibo
  const discount = Math.min(rawDiscount, subtotal);
  const discountedSubtotal = Math.max(0, subtotal - discount);
  // original tax theke tax rate ber korchi
  const taxRate = subtotal > 0 ? originalTax / subtotal : 0;
  // coupon apply korar por new subtotal er upor tax
  const totalTax = discountedSubtotal * taxRate;
  const effectiveShippingCharge = discountedSubtotal >= 35 ? 0 : shippingCharge;
  const total = Math.max(0, discountedSubtotal + effectiveShippingCharge + totalTax);
  const handleField = (e) => {
    const { name, value } = e.target;

    setForm((prev) => {
      if (name === "country") {
        return {
          ...prev,
          country: value,
          state: "",
          postCode: "",
        };
      }

      return {
        ...prev,
        [name]: value,
      };
    });
  };

  const handleApplyCoupon = async () => {
    if (!couponInput.trim()) {
      setCouponError("Please enter a coupon code.");
      return;
    }

    setCouponError("");
    setCouponData(null);
    setCouponLoading(true);

    try {
      const res = await CouponServices.checkCoupon(couponInput.trim());

      setCouponData({
        ...res.data,
        code: res.data?.coupon,
        discount: Number(res.data?.discount || 0),
        percentage: Number(res.data?.percentage || 0),
        total_paid_amount: res.total_paid_amount,
        message: res.message,
      });

      setCouponError("");
    } catch (err) {
      setCouponData(null);
      setCouponError(
        err?.response?.data?.message || "Invalid coupon."
      );
    } finally {
      setCouponLoading(false);
    }
  };

  const handlePlaceOrder = async () => {
    if (!orderItems.length) {
      setError("No items to order.");
      return;
    }

    setError("");

    if (!form.firstName.trim()) return setError("First name is required.");
    if (!form.lastName.trim()) return setError("Last name is required.");
    if (!form.country) return setError("Country is required.");
    if (!form.address.trim()) return setError("Address is required.");
    if (!form.city.trim()) return setError("City is required.");
    if (!form.state) return setError("State is required.");
    if (!form.postCode.trim()) return setError("Post code is required.");
    if (!form.phone.trim()) return setError("Phone is required.");
    if (!form.email.trim()) return setError("Email is required.");

    const cartItemIds = orderItems
      .map((item) => parseInt(item.id, 10))
      .filter((id) => !isNaN(id) && id > 0);

    if (cartItemIds.length === 0) {
      setError("No valid cart items found.");
      return;
    }

    setPlacing(true);

    try {
      const orderPayload = {
        first_name: form.firstName.trim(),
        last_name: form.lastName.trim(),
        business_name: form.businessName.trim() || null,
        country: selectedLocationObj?.name || "",
        address: form.address.trim(),
        address_line2: form.addressLine2.trim() || null,
        city: form.city.trim(),
        state: selectedStateObj?.state_name || "",
        post_code: form.postCode.trim(),
        phone: String(form.phone).trim(),
        email: form.email.trim(),
        notes: form.notes.trim() || null,
        payment_method: selectedPayment,

        coupon_code: couponData?.code ?? null,
        subtotal: subtotal,
        discount: discount,
        tax: totalTax,
        total: total,

        cart_item_ids: cartItemIds,
        location_id: selectedLocationObj?.id ?? null,
        state_id: selectedStateObj?.id ?? null,
        shipping_charge: effectiveShippingCharge,
      };

      if (selectedPayment === "stripe") {
        setPendingOrderPayload(orderPayload);
        setStep("stripe");
        setPlacing(false);
        return;
      }

      const res = await OrderServices.placeOrder(orderPayload);

      window.dispatchEvent(new Event("cart_updated"));

      setOrderSuccess({
        order_id: res.order_id,
        total: res.total ?? total,
        payment_method: res.payment_method,
        status: res.status,
        invoice_number: res.invoice_number,
        email: form.email.trim(),
      });

      setStep("success");
    } catch (err) {
      console.error("Place order error:", err?.response?.data || err);

      const backendErrors = err?.response?.data?.errors;
      if (backendErrors) {
        const firstError = Object.values(backendErrors).flat()[0];
        setError(firstError);
      } else {
        setError(
          err?.response?.data?.message ||
            "Failed to place order. Please try again."
        );
      }
    } finally {
      setPlacing(false);
    }
  };

  const handleStripeSuccess = async (paymentIntent) => {
    if (!pendingOrderPayload) {
      setError("Order details missing. Please try again.");
      setStep("form");
      return;
    }

    setPlacing(true);
    setError("");

    try {
      const res = await OrderServices.placeOrder({
        ...pendingOrderPayload,
        payment_method: "stripe",
        payment_intent_id: paymentIntent.id,
      });

      window.dispatchEvent(new Event("cart_updated"));

      setOrderSuccess({
        order_id: res.order_id,
        total: res.total ?? total,
        payment_method: "stripe",
        status: res.status || "processing",
        invoice_number: res.invoice_number,
        email: pendingOrderPayload.email,
      });

      setStep("success");
    } catch (err) {
      setError(
        err?.response?.data?.message ||
          "Payment succeeded, but order creation failed. Please contact support."
      );
    } finally {
      setPlacing(false);
    }
  };

  const handleStripeError = (msg) => {
    setError(msg);
  };

  if (step === "success" && orderSuccess) {
    return <OrderConfirm order={orderSuccess} currencySymbol={currencySymbol} />;
  }

  const formFields = [
    { name: "firstName", col: "col-sm-6", type: "text", placeholder: "First Name *" },
    { name: "lastName", col: "col-sm-6", type: "text", placeholder: "Last Name *" },
    { name: "businessName", col: "col-12", type: "text", placeholder: "Business Name (optional)" },
    { name: "address", col: "col-12", type: "text", placeholder: "Street address *" },
    { name: "addressLine2", col: "col-12", type: "text", placeholder: "Apt, suite, unit, etc. (optional)" },
    { name: "city", col: "col-sm-6", type: "text", placeholder: "Town / City *" },
    { name: "phone", col: "col-12", type: "number", placeholder: "Phone *" },
    { name: "email", col: "col-12", type: "email", placeholder: "Email Address *" },
  ];

  const payments = [
    { id: "stripe", label: "💳 Credit / Debit Card (Stripe)" },
    // { id: "paypal", label: "💰 PayPal" },
    // { id: "bank_transfer", label: "🏦 Direct Bank Transfer" },
    // { id: "cod", label: "💵 Cash on Delivery" },
  ];

  const paymentDescriptions = {
    stripe: "Pay securely with your credit or debit card via Stripe.",
    paypal: "Pay securely with your PayPal account or credit card.",
    bank_transfer:
      "Make your payment directly into our bank account. Please use your Order ID as the payment reference.",
    cod: "Pay with cash when your order is delivered to your door.",
  };

  return (
    <section className="checkout py-80">
      <style>{CheckoutCSS}</style>
      <div className="container container-lg">
        <div
          className="border border-gray-100 rounded-8 px-24 px-sm-30 py-20 mb-40
                      d-flex align-items-center justify-content-between flex-wrap gap-16"
        >
          <span style={{ fontSize: 13.5, color: "#475467" }}>
            Have a coupon?
          </span>

          <div className="d-flex gap-12 flex-wrap align-items-center">
            {couponData ? (
              <div className="co-coupon-badge">
                ✅ {couponData.code} —{" "}
                {Number(couponData.discount || 0) > 0
                  ? `${money(couponData.discount, currencySymbol)} off`
                  : `${Number(couponData.percentage || 0)}% off`}

                <button
                  type="button"
                  onClick={() => {
                    setCouponData(null);
                    setCouponInput("");
                    setCouponError("");
                  }}
                  style={{
                    background: "none",
                    border: "none",
                    cursor: "pointer",
                    color: "#dc2626",
                    fontWeight: 700,
                  }}
                >
                  ✕
                </button>
              </div>
            ) : (
              <>
                <input
                  type="text"
                  className="co-input"
                  style={{ maxWidth: 200 }}
                  placeholder="Coupon code"
                  value={couponInput}
                  onChange={(e) => {
                    setCouponInput(e.target.value);
                    setCouponError("");
                  }}
                />
                <button
                  type="button"
                  className="btn btn-main py-12 px-24 rounded-8 flex-shrink-0"
                  onClick={handleApplyCoupon}
                  disabled={couponLoading}
                >
                  {couponLoading ? "Checking..." : "Apply Coupon"}
                </button>
              </>
            )}

            <div style={{ width: "100%", marginTop: 8 }}>
              {couponError && (
                <div
                  style={{
                    fontSize: 12,
                    color: "#dc2626",
                    fontWeight: 600,
                  }}
                >
                  {couponError}
                </div>
              )}

              {couponData && !couponError && (
                <div
                  style={{
                    fontSize: 12,
                    color: "#166534",
                    fontWeight: 600,
                  }}
                >
                  Coupon applied successfully.
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="row gy-4">
          <div className="col-xl-9 col-lg-8">
            <div className="border border-gray-100 rounded-8 px-24 px-sm-32 py-32 py-sm-40">
              {step === "stripe" ? (
                <>
                  <div className="co-toolbar">
                    <span className="co-toolbar-title">💳 Complete Your Payment</span>
                  </div>
                  <p style={{ fontSize: 13.5, color: "#475467", marginBottom: 20 }}>
                    Enter your card details to complete payment. Your order will be created after payment is successful.
                  </p>

                  <Elements stripe={getStripePromise()}>
                    <StripePaymentForm
                      paymentPayload={{
                        amount: total,
                        currency: "usd",
                      }}
                      onSuccess={handleStripeSuccess}
                      onError={handleStripeError}
                    />
                  </Elements>

                  {error && <div className="co-error">{error}</div>}
                </>
              ) : (
                <>
                  <div className="co-toolbar">
                    <span className="co-toolbar-title">🏠 Billing Details</span>
                    <span className="co-label">Fields marked * are required</span>
                  </div>

                  <div className="row gy-3">
                    {formFields.map((f) => (
                      <div className={f.col} key={f.name}>
                        <input
                          type={f.type}
                          name={f.name}
                          value={form[f.name]}
                          onChange={handleField}
                          className="co-input"
                          placeholder={f.placeholder}
                        />
                      </div>
                    ))}

                    <div className="col-sm-6">
                      <select
                        name="country"
                        value={form.country}
                        onChange={handleField}
                        className="co-input"
                        disabled={locationLoading}
                      >
                        <option value="">
                          {locationLoading
                            ? "Loading countries..."
                            : "Select Country / Region *"}
                        </option>
                        {locations.map((loc) => (
                          <option key={loc.id} value={loc.id}>
                            {loc.name}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="col-sm-6">
                      <select
                        name="state"
                        value={form.state}
                        onChange={handleField}
                        className="co-input"
                        disabled={!form.country || filteredStates.length === 0}
                      >
                        <option value="">
                          {!form.country
                            ? "Select country first *"
                            : filteredStates.length === 0
                            ? "No states available"
                            : "Select State / Province *"}
                        </option>
                        {filteredStates.map((st) => (
                          <option key={st.id} value={st.id}>
                            {st.state_name}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="col-sm-6">
                      <input
                        type="text"
                        name="postCode"
                        value={form.postCode}
                        onChange={handleField}
                        className="co-input"
                        placeholder="ZIP / Post Code *"
                      />
                    </div>
                  </div>

                  <div className="co-toolbar mt-40" style={{ marginBottom: 16 }}>
                    <span className="co-toolbar-title">📝 Additional Information</span>
                  </div>

                  <textarea
                    name="notes"
                    value={form.notes}
                    onChange={handleField}
                    className="co-input"
                    placeholder="Notes about your order, e.g. special notes for delivery."
                    rows={4}
                  />

                  {error && <div className="co-error mt-20">{error}</div>}
                </>
              )}
            </div>
          </div>

          <div className="col-xl-3 col-lg-4">
            <div style={{ position: "sticky", top: 20, fontFamily: "'DM Sans', sans-serif" }}>

              {/* Order panel */}
              <div className="op-panel">
                <div className="op-head">
                  <p className="op-title">Order summary</p>
                  <p className="op-sub">{orderItems.length} item{orderItems.length !== 1 ? "s" : ""}</p>
                </div>

                {loading ? (
                  <div style={{ padding: "32px 20px", textAlign: "center" }}>
                    <div className="spinner-border" style={{ color: "#1D9E75", width: 24, height: 24 }} role="status" />
                    <div style={{ fontSize: 12, color: "#98a2b3", marginTop: 10, fontWeight: 500 }}>Loading items…</div>
                  </div>
                ) : orderItems.length === 0 ? (
                  <div style={{ padding: "24px 20px" }}>
                    <p className="co-hint">No items selected.</p>
                    <Link to="/cart" className="btn btn-outline-main w-100 py-14 rounded-8">← Back to Cart</Link>
                  </div>
                ) : (
                  <>
                    {/* Items */}
                    <div>
                      {orderItems.map((item) => {
                        const qty = Number(item?.quantity ?? 1);
                        const price = resolveUnitPrice(item);
                        const tax = resolveUnitTax(item);
                        const isFlash = hasFlashSale(item);
                        return (
                          <div className="op-item" key={item.id}>
                            <div className="op-img">
                              <img
                                src={resolveImage(item)}
                                alt={resolveTitle(item)}
                                style={{ width: 48, height: 48, objectFit: "cover", borderRadius: 8 }}
                                onError={(e) => { e.currentTarget.src = "/assets/images/thumbs/product-thumb-1.png"; }}
                              />
                            </div>
                            <div className="op-item-info">
                              <p className="op-item-name">{resolveTitle(item)}{isFlash && <span className="co-flash-badge">🔥 FLASH</span>}</p>
                              <p className="op-item-qty">Qty: {qty}</p>
                            </div>
                            <div style={{ textAlign: "right", flexShrink: 0 }}>
                              <div className={`op-price${isFlash ? " flash" : ""}`}>{money(price * qty, currencySymbol)}</div>
                              {tax > 0 && <div style={{ fontSize: 11, color: "#98a2b3", fontFamily: "'DM Mono', monospace" }}>+{money(tax * qty, currencySymbol)} tax</div>}
                            </div>
                          </div>
                        );
                      })}
                    </div>

                    {/* Summary lines */}
                    <div className="op-summary">
                      <div className="op-line">
                        <span className="op-line-lbl">Subtotal</span>
                        <span className="op-line-val">{money(subtotal, currencySymbol)}</span>
                      </div>
                      {couponData && (
                        <div className="op-line">
                          <span className="op-line-lbl">Discount ({couponData.code})</span>
                          <span className="op-line-val disc">−{money(discount, currencySymbol)}</span>
                        </div>
                      )}
                      <div className="op-line">
                        <span className="op-line-lbl">Shipping</span>
                        <span className={`op-line-val${effectiveShippingCharge === 0 && shippingCharge > 0 ? " free" : ""}`}>
                          {selectedStateObj
                            ? effectiveShippingCharge === 0 && shippingCharge > 0
                              ? "✓ Free"
                              : money(effectiveShippingCharge, currencySymbol)
                            : "—"}
                        </span>
                      </div>
                      <div className="op-line">
                        <span className="op-line-lbl">Tax</span>
                        <span className="op-line-val">{money(totalTax, currencySymbol)}</span>
                      </div>
                      <div className="op-line op-total-line">
                        <span className="op-line-lbl">Total</span>
                        <span className="op-line-val">{money(total, currencySymbol)}</span>
                      </div>
                    </div>

                    {/* Free shipping / nudge banner */}
                    {selectedStateObj && effectiveShippingCharge === 0 && shippingCharge > 0 && (
                      <div className="op-free-bar">
                        <span>🎉 Free shipping unlocked on this order</span>
                      </div>
                    )}
                    {selectedStateObj && effectiveShippingCharge > 0 && discountedSubtotal < 35 && (
                      <div className="op-near-bar">
                        <span>Add {money(35 - discountedSubtotal, currencySymbol)} more for free shipping</span>
                      </div>
                    )}

                    {/* Location */}
                    {selectedLocationObj && selectedStateObj && (
                      <div className="op-location">
                        <div className="op-loc">
                          <span className="op-loc-lbl">Country</span>
                          <span className="op-loc-val">{selectedLocationObj.name}</span>
                        </div>
                        <div className="op-loc">
                          <span className="op-loc-lbl">State</span>
                          <span className="op-loc-val">{selectedStateObj.state_name}</span>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>

              {/* Payment methods */}
              {step !== "stripe" && orderItems.length > 0 && (
                <div className="pm-section">
                  <p className="pm-head">Payment method</p>

                  {payments.map((p) => (
                    <div
                      key={p.id}
                      className={`pm-item${selectedPayment === p.id ? " pm-active" : ""}`}
                      onClick={() => setSelectedPayment(p.id)}
                    >
                      <div className="pm-row">
                        <div className="pm-radio" />
                        <div className="pm-icon">
                          {p.id === "stripe" && <span style={{ fontSize: 13 }}>💳</span>}

                          {p.id === "paypal" && (
                            <span
                              style={{
                                fontSize: 10,
                                fontWeight: 700,
                                color: "#003087",
                                letterSpacing: -0.3,
                              }}
                            >
                              Pay<span style={{ color: "#009cde" }}>Pal</span>
                            </span>
                          )}

                          {p.id === "cod" && <span style={{ fontSize: 13 }}>💵</span>}
                        </div>

                        <span className="pm-name">
                          {p.id === "stripe"
                            ? "Credit / Debit Card"
                            : p.id === "paypal"
                            ? "PayPal"
                            : "Cash on delivery"}
                        </span>

                        {p.id === "paypal" && <span className="pm-badge">Secure</span>}
                        {p.id === "stripe" && <span className="pm-badge">Secure</span>}
                        {p.id === "paypal" && <span className="pm-badge">Secure</span>}
                      </div>
                      {selectedPayment === p.id && (
                        <div className="pm-desc">{paymentDescriptions[p.id]}</div>
                      )}
                    </div>
                  ))}

                  <button
                    className="place-btn"
                    disabled={placing}
                    onClick={handlePlaceOrder}
                  >
                    {placing
                      ? <><div className="btn-spinner" /> Placing order…</>
                      : <>🔒 Place order — {money(total, currencySymbol)}</>
                    }
                  </button>

                  <div className="secure-row">
                    <span>256-bit SSL · PCI DSS compliant</span>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Checkout;